/**
 * main.jsx — Entry React FG-OS
 *
 * Responsabilites :
 *   1. Init du theme AVANT le render (evite FOUC = flash of unstyled content)
 *   2. Pre-chargement du logo PDF (warm-up promise) en arriere-plan
 *   3. Montage de l'app React dans #root
 *
 * Strict mode active en dev pour detecter side-effects non-puissants
 * (double-render intentionnel, deteche les hooks mal ecrits).
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import App from './App.jsx';
import { applyTheme } from './lib/tokens.js';
import { getTheme } from './lib/storage.js';

// ============================================================
// 1. Resolution du theme initial
// ============================================================
//
// DESIGN.md : light default FORCE, dark optionnel manuel uniquement.
// Pas de fallback systeme (prefers-color-scheme ignore par design).
// L'utilisateur choisit explicitement via le Module Reglages.
//
// Applique AVANT createRoot pour eviter le flash blanc -> sombre au mount.

function resolveInitialTheme() {
  const stored = getTheme();
  if (stored === 'light' || stored === 'dark') return stored;
  return 'light';
}

const initialTheme = resolveInitialTheme();
applyTheme(initialTheme);

// ============================================================
// 2. Pre-chargement non bloquant du logo PDF
// ============================================================
//
// Le module pdf.js declenche deja un warm-up automatique au load
// (cf if typeof window !== 'undefined' loadLogoOnce()). On l'importe
// dynamiquement ici pour eviter d'inclure jspdf dans le bundle critique :
// le code-split Vite mettra pdf.js (et jspdf) dans un chunk separe,
// telecharge en arriere-plan apres la 1ere peinture.

if (typeof window !== 'undefined') {
  // requestIdleCallback si dispo, sinon setTimeout 0 (Safari)
  const schedule =
    typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback
      : (cb) => window.setTimeout(cb, 0);

  schedule(() => {
    import('./lib/pdf.js')
      .then((mod) => mod.preloadLogo())
      .catch(() => {
        /* silent : pdf utilise au moment du Chiffrage, logo non bloquant ici */
      });
  });
}

// ============================================================
// 3. Montage React
// ============================================================

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error("FG-OS: element <div id=\"root\"> introuvable dans index.html");
}

createRoot(rootEl).render(
  <StrictMode>
    <App initialTheme={initialTheme} />
  </StrictMode>,
);
